import { FaroRoutes } from '@grafana/faro-react';
import { useFlag } from '@unleash/proxy-client-react';
import { Navigate, Route } from 'react-router-dom';
import { FeatureToggle } from 'utils/unleash/toggles';

import { usePermissionConfig } from 'context';

import {
  Activities,
  CareProviderAgendaAvailability,
  CareProviderAgendaCalendar,
  CareProviderDashboard,
  CareProviderInfo,
  Call,
  Chat,
  ClinicalAssessmentAnswers,
  ClinicalAssessments,
  Content,
  EndUser,
  Logbook,
  Login,
  NotFound,
  Payments,
  PreAssessment,
  Program,
  SessionHistory,
  TherapyCompanionDashboard,
} from 'pages';
import ContentLibrary from 'pages/ContentLibrary';
import { Meet } from 'pages/Meet';
import PatientList from 'pages/PatientList';
import SignOut from 'pages/SignOut';

import { RouteProps } from './types';

const Routes: React.FC = () => {
  const conditionalPaths = usePermissionConfig().getPermissionRoutes();
  const newLoginFlowEnabled = useFlag(FeatureToggle.NewLoginFlow);

  const routes: RouteProps[] = [
    {
      path: '/signout',
      name: 'PAGE.NAME.SIGNOUT',
      Element: SignOut,
    },
    {
      path: '/',
      name: 'PAGE.NAME.HOME',
      Element: () => <Navigate to="/care-provider/info" />,
    },
    {
      path: '/home',
      name: 'PAGE.NAME.HOME',
      Element: () => <Navigate to="/care-provider/info" />,
    },
    {
      path: '/login/:step?',
      name: 'Login',
      Element: Login,
    },
    {
      path: '/enduser/:id',
      name: 'TABLIST.OVERVIEW',
      Element: EndUser,
    },
    {
      path: '/enduser/:id/logbook',
      name: 'PAGE.NAME.LOGBOOK',
      Element: Logbook,
    },
    {
      path: '/enduser/:id/pre-assessment',
      name: 'TABLIST.PREASSESSMENT',
      Element: PreAssessment,
    },
    {
      path: '/enduser/:id/clinical-assessment',
      name: 'TABLIST.CLINICALASSESSMENT',
      Element: ClinicalAssessments,
    },
    {
      path: '/enduser/:id/activities',
      name: 'Atividades',
      Element: Activities,
    },
    {
      path: '/enduser/:id/clinical-assessment/:submissionId',
      name: 'Avaliações Clínicas - Resultado',
      Element: ClinicalAssessmentAnswers,
    },
    {
      path: '/care-provider/patient-list',
      name: 'Care Provider - Patient List',
      Element: PatientList,
    },
    {
      path: '/care-provider/info',
      name: 'Care Provider - Info',
      Element: CareProviderInfo,
    },
    {
      path: '/care-provider/session-history',
      name: 'Care Provider - Session History',
      Element: SessionHistory,
    },
    {
      path: '/care-provider/session-history/year/:year/month/:month',
      name: 'Care Provider - Session History',
      Element: () => <Navigate to="/care-provider/session-history" />,
    },
    {
      path: '/content-library',
      name: 'Content Library',
      Element: ContentLibrary,
    },
    {
      path: '/content-library/programs/:programId',
      name: 'Content Library - Program',
      Element: Program,
    },
    {
      path: '/content-library/programs/:programId/pages/:pageId',
      name: 'Content',
      Element: Content,
    },
    {
      path: '/care-provider/agenda/calendar',
      name: 'Care Provider - Calendar',
      Element: CareProviderAgendaCalendar,
    },
    {
      path: '/care-provider/agenda/availability',
      name: 'Care Provider - Availability',
      Element: CareProviderAgendaAvailability,
    },
    {
      path: '/care-provider/chat',
      name: 'Care Provider - Chat',
      Element: Chat,
    },
    {
      path: '/care-provider/meet',
      name: 'Care Provider - Meet',
      Element: Meet,
      hideMenu: true,
    },
    {
      path: '/care-provider/dashboard',
      name: 'Care Provider - Dashboard',
      Element: CareProviderDashboard,
    },
    {
      path: '/therapy-companion/dashboard',
      name: 'Therapy Companion - Dashboard',
      Element: TherapyCompanionDashboard,
    },
    {
      path: '/care-provider/payments',
      name: 'Care Provider - Payments',
      Element: Payments,
    },
    {
      path: '/care-provider/payments/year/:year/month/:month',
      name: 'Care Provider - Payments',
      Element: () => <Navigate to="/care-provider/payments" />,
    },
    {
      path: '/care-provider/daily-call',
      name: 'Care Provider - Call',
      Element: Call,
    },
    ...conditionalPaths,
    {
      path: '/*',
      name: 'PAGE.NAME.NOTFOUND',
      Element: NotFound,
    },
  ];

  const filteredRoutes = newLoginFlowEnabled
    ? routes.filter(({ path }) => path !== '/login/:step?')
    : routes;

  return (
    <FaroRoutes>
      {filteredRoutes.map(({ path, Element, hideMenu = false }) => {
        return (
          <Route
            path={path}
            key={path}
            element={<Element hideMenu={hideMenu} />}
          />
        );
      })}
    </FaroRoutes>
  );
};

export default Routes;
